import React, { useState } from "react";
import Modal from "react-modal";
import ActiveStep from "./ActiveStep";
import { ProcessAction, ProcessStep } from "../GuidedProcess.types";

const customStyles = {
  content: {
    left: "50%",
    right: "auto",
    bottom: "auto",
    backgroundColor: "white",
    maxWidth: "764px",
    maxHeight: "621px",
    height: "calc(100vh - 200px)",
    width: "calc(100vw - 40px)",
    borderRadius: "4px",
    position: "absolute",
    boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
    transform: "translate(-50%, -50%)",
    justifyContent: "space-between",
    flexDirection: "column",
    display: "flex",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
};

type SelectedStepProps = {
  step: ProcessStep;
  action: ProcessAction;
  index: number;
  updateSelectedAction: (nextAction: ProcessAction) => void;
};

const SelectedStep: React.FC<SelectedStepProps> = ({
  step,
  action,
  index,
  updateSelectedAction,
}) => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const [showChangeWarning, setShowChangeWarning] = useState<boolean>(false);
  const [manuallyChangedAction, setManuallyChangedAction] =
    useState<ProcessAction | null>(null);
  const [showEditAnswer, setShowEditAnswer] = useState<boolean>(false);

  return (
    <div className="guided-process__step previous-step" key={index}>
      <p style={{ margin: "0 0 8px 0" }}>Let's see what happens if:</p>
      <div className="guided-process__step--inner">
        <div className="previous-step__header">
          <h6>{action.title}</h6>
          <p
            className="change-selection"
            onClick={() => setShowEditAnswer(true)}
          >
            Change selection
            <svg
              width="17"
              height="17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.656 1.281a2.226 2.226 0 0 1 3.156 0l.407.407a2.226 2.226 0 0 1 0 3.156L9.03 11.03a2.15 2.15 0 0 1-.969.563l-3.124.906a.708.708 0 0 1-.72-.219.708.708 0 0 1-.218-.719l.906-3.124a2.15 2.15 0 0 1 .563-.97l6.187-6.187Zm2.094 1.063a.736.736 0 0 0-1.031 0l-.938.906 1.469 1.469.906-.938a.736.736 0 0 0 0-1.031l-.406-.406Zm-7.406 6.5-.532 1.844 1.844-.532a.648.648 0 0 0 .313-.187l4.218-4.219-1.437-1.438-4.219 4.22a.648.648 0 0 0-.187.312ZM6.25 2.5a.76.76 0 0 1 .75.75.74.74 0 0 1-.75.75h-3.5c-.719 0-1.25.563-1.25 1.25v8.5c0 .719.531 1.25 1.25 1.25h8.5c.688 0 1.25-.531 1.25-1.25v-3.5a.74.74 0 0 1 .75-.75.76.76 0 0 1 .75.75v3.5c0 1.531-1.25 2.75-2.75 2.75h-8.5A2.734 2.734 0 0 1 0 13.75v-8.5C0 3.75 1.219 2.5 2.75 2.5h3.5Z"
                fill="#475569"
              />
            </svg>
          </p>

          <Modal
            isOpen={showEditAnswer}
            onRequestClose={() => setShowEditAnswer(false)}
            style={customStyles}
            closeTimeoutMS={600}
            ariaHideApp={false}
            className="moj-modal mjb"
            contentLabel="Chane answer page"
          >
            <div className="moj-modal__content">
              <ActiveStep
                step={step}
                selectedActionLabel={
                  manuallyChangedAction
                    ? manuallyChangedAction.label
                    : action.label
                }
                hideContent={true}
                handleNextStep={(actionIndex, nextStepId) => {
                  if (nextStepId !== action.nextStepId) {
                    // we have a changed action value, so we need to update the selected action
                    setManuallyChangedAction(step.nextSteps[actionIndex]);
                    setShowChangeWarning(true);
                  } else {
                    setManuallyChangedAction(null);
                  }
                }}
              />

              {showChangeWarning && (
                <div
                  className="block-alert block-alert--warning rte"
                  style={{ marginTop: 32, marginBottom: 0 }}
                >
                  <p>
                    <strong>Are you sure?</strong>
                    <br />
                    This selection will show you a different pathway. Your
                    previous answers will no longer be available.
                  </p>
                </div>
              )}
            </div>

            <div className="moj-modal__footer">
              <button
                type="reset"
                className="moj-modal__footer-button moj-modal__footer-button--outline"
                onClick={() => {
                  setShowChangeWarning(false);
                  setShowEditAnswer(false);
                }}
              >
                Back
              </button>

              {manuallyChangedAction && (
                <button
                  className="moj-modal__footer-button"
                  onClick={() => {
                    setShowChangeWarning(false);
                    setShowEditAnswer(false);
                    updateSelectedAction(manuallyChangedAction);
                  }}
                >
                  Continue
                </button>
              )}
            </div>
          </Modal>
        </div>

        {action.content && (
          <div
            className={`previous-step__accordion ${expanded ? "expanded" : ""}`}
          >
            <div
              className="previous-step__accordion--header"
              onClick={() => {
                setExpanded(!expanded);
              }}
            >
              <h5>{action.contentTitle}</h5>

              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="icon-svg"
              >
                <path
                  d="M18.8814 8.72727L18.2806 8.09486C18.1225 7.96838 17.8696 7.96838 17.7431 8.09486L12.0198 13.8182C12.0198 13.8182 6.3913 8.22134 6.26482 8.09486C6.13834 7.96838 5.88538 7.96838 5.72727 8.09486L5.12648 8.72727C4.96838 8.88538 4.96838 9.10672 5.12648 9.26482L11.7352 15.9051C11.8933 16.0316 12.1146 16.0316 12.2727 15.9051L18.8814 9.26482C19.0395 9.10672 19.0395 8.88538 18.8814 8.72727Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div
              className="previous-step__accordion--body rte rte--small"
              dangerouslySetInnerHTML={{ __html: action.content }}
            ></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectedStep;
